import { createContext, useContext, useRef, useCallback, useState } from 'react';

/**
 * WizardModeContext
 *
 * When active (isWizardMode = true), settings pages embedded inside the
 * onboarding wizard:
 *   1. Hide their sticky footer save bar (Save/Cancel buttons).
 *   2. Register their save function so the wizard's Next button can call it.
 */

interface WizardModeContextValue {
  isWizardMode: boolean;
  /**
   * Called by embedded settings pages to register their save function.
   * Returns a disposer that clears the registration, so a page unmounting
   * does not leave its save function attached to a later step.
   */
  registerSave: (fn: () => Promise<void>) => () => void;
  /** Called by the wizard Next button to trigger the active step's save. */
  triggerSave: () => Promise<void>;
}

const WizardModeContext = createContext<WizardModeContextValue>({
  isWizardMode: false,
  registerSave: () => () => {},
  triggerSave: async () => {},
});

export function useWizardMode() {
  return useContext(WizardModeContext);
}

/**
 * Wrap the wizard shell with this provider so all embedded settings pages
 * inherit wizard mode automatically.
 */
export function WizardModeProvider({ children }: { children: React.ReactNode }) {
  const saveFnRef = useRef<(() => Promise<void>) | null>(null);
  // Track registrations so components can re-register on mount
  const [, forceUpdate] = useState(0);

  const registerSave = useCallback((fn: () => Promise<void>) => {
    saveFnRef.current = fn;
    forceUpdate(n => n + 1);
    return () => {
      if (saveFnRef.current === fn) {
        saveFnRef.current = null;
      }
    };
  }, []);

  const triggerSave = useCallback(async () => {
    if (saveFnRef.current) {
      await saveFnRef.current();
    }
  }, []);

  return (
    <WizardModeContext.Provider value={{ isWizardMode: true, registerSave, triggerSave }}>
      {children}
    </WizardModeContext.Provider>
  );
}
